import { parseJsonWithNativeSafeNumbers } from '@pillar-portal/api-types';
import { useState, type ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BackButton } from '@/components/ui/back-button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { organizationApi } from '@/lib/pillarApi';
import {
    MerchantBackupSchema,
    type MerchantBackup,
    type MerchantBackupCollectionName,
    type MerchantBackupRestoreCollectionSummary,
    type RestoreMerchantBackupDryRunResponse,
} from '@pillar-portal/api-types';

type MerchantBackupSummaryRow = {
    readonly key: MerchantBackupCollectionName;
    readonly label: string;
};

type MerchantBackupSummaryAction = keyof MerchantBackupRestoreCollectionSummary;

const MERCHANT_BACKUP_SUMMARY_ROWS: readonly MerchantBackupSummaryRow[] = [
    { key: 'organizations', label: 'Organizations' },
    { key: 'users', label: 'Users' },
    { key: 'permissions_bundles', label: 'Permission bundles' },
    { key: 'role_assignments', label: 'Role assignments' },
    { key: 'permissions_policy_grants', label: 'Policy grants' },
    { key: 'credentials', label: 'Credentials' },
    { key: 'merchant_accounts', label: 'Merchant accounts' },
    { key: 'logical_terminals', label: 'Logical terminals' },
    { key: 'terminal_devices', label: 'Terminal devices' },
    { key: 'passwords', label: 'Passwords' },
    { key: 'totp_secrets', label: 'TOTP secrets' },
    { key: 'totp_backup_codes', label: 'TOTP backup codes' },
    { key: 'document_templates', label: 'Document templates' },
    { key: 'legal_documents', label: 'Legal documents' },
    { key: 'processors', label: 'Processors' },
    { key: 'payment_method_tokens', label: 'Payment method tokens' },
    { key: 'stored_payment_methods', label: 'Stored payment methods' },
    { key: 'credit_card_stored_payment_details', label: 'Credit card details' },
    { key: 'debit_card_stored_payment_details', label: 'Debit card details' },
    { key: 'ach_stored_payment_details', label: 'ACH details' },
    { key: 'payment_transactions', label: 'Payment transactions' },
    { key: 'payment_requests', label: 'Payment requests' },
    { key: 'payment_request_delivery_events', label: 'Payment request delivery events' },
    { key: 'payment_request_documents', label: 'Payment request documents' },
    {
        key: 'payment_transaction_lifecycle_events',
        label: 'Payment transaction lifecycle events',
    },
    { key: 'ipw_customer_links', label: 'Customer portal links' },
    { key: 'merchant_customer_records', label: 'Merchant customer records' },
    { key: 'user_legal_document_acceptances', label: 'Legal document acceptances' },
    { key: 'ipx_credential_definitions', label: 'Credential definitions' },
    { key: 'ipx_web_resource_models', label: 'Web resource models' },
    { key: 'allowed_website_templates', label: 'Allowed website templates' },
    { key: 'organization_allowed_websites', label: 'Organization allowed websites' },
    { key: 'organization_rpa_builder_websites', label: 'Organization RPA Builder websites' },
    { key: 'ipx_rbs_workflow_instances', label: 'Workflow instances' },
    { key: 'organization_ipx_credential_policies', label: 'Organization credential policies' },
    { key: 'credential_rotation_attempts', label: 'Credential rotation attempts' },
    { key: 'payment_request_access_tokens', label: 'Payment request access tokens' },
];

const MERCHANT_BACKUP_SUMMARY_ACTIONS: readonly MerchantBackupSummaryAction[] = [
    'create',
    'reuse',
    'prerequisite',
    'excluded',
    'warning',
    'error',
];

function formatRestoreError(error: unknown, fallbackMessage: string): string {
    if (error instanceof Error) {
        return error.message;
    }
    return fallbackMessage;
}

export default function MerchantBackupPage() {
    const [selectedBackup, setSelectedBackup] = useState<MerchantBackup | null>(null);
    const [selectedFilename, setSelectedFilename] = useState<string | null>(null);
    const [parseErrorMessage, setParseErrorMessage] = useState<string | null>(null);
    const [showApplyConfirmation, setShowApplyConfirmation] = useState<boolean>(false);

    const dryRunMutation = useMutation({
        mutationFn: async (backup: MerchantBackup): Promise<RestoreMerchantBackupDryRunResponse> =>
            await organizationApi.restoreMerchantBackupDryRun({ backup }),
    });

    const applyMutation = useMutation({
        mutationFn: async (backup: MerchantBackup) =>
            await organizationApi.restoreMerchantBackupApply({ backup }),
    });

    const onFileSelected = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const selectedFile: File | undefined = event.target.files?.[0];
        if (selectedFile === undefined) {
            return;
        }
        setParseErrorMessage(null);
        setSelectedBackup(null);
        setSelectedFilename(selectedFile.name);
        dryRunMutation.reset();
        applyMutation.reset();
        try {
            const fileContents: string = await selectedFile.text();
            const parsedJson: unknown = parseJsonWithNativeSafeNumbers(fileContents);
            const parsedBackup: MerchantBackup = MerchantBackupSchema.parse(parsedJson);
            setSelectedBackup(parsedBackup);
        } catch (error: unknown) {
            const errorMessage: string =
                error instanceof Error ? error.message : 'Failed to parse backup file.';
            setParseErrorMessage(errorMessage);
        }
    };

    const runDryRun = (): void => {
        if (selectedBackup === null) {
            return;
        }
        dryRunMutation.mutate(selectedBackup);
    };

    const confirmApplyRestore = (): void => {
        if (selectedBackup === null) {
            return;
        }
        applyMutation.mutate(selectedBackup);
        setShowApplyConfirmation(false);
    };

    const dryRunResult: RestoreMerchantBackupDryRunResponse | undefined = dryRunMutation.data;
    const applyRestoreDisabled: boolean =
        dryRunResult === undefined || !dryRunResult.valid || applyMutation.isPending;
    const applyRestoreTooltip: string = (() => {
        if (dryRunResult === undefined) {
            return 'Run a dry run before applying restore.';
        }
        if (!dryRunResult.valid) {
            return 'Resolve dry-run errors before applying restore.';
        }
        if (applyMutation.isPending) {
            return 'Restore is already running.';
        }
        return 'Apply restore';
    })();

    return (
        <div className="space-y-6">
            <BackButton href="/dashboard" tooltip="Back to Dashboard" />
            <h1 className="text-3xl font-bold">Load Merchant Backup</h1>

            <Card>
                <CardHeader>
                    <CardTitle>Select Backup File</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <input type="file" accept="application/json" onChange={onFileSelected} />
                    {selectedFilename !== null ? (
                        <div className="text-sm text-muted-foreground">
                            Selected file: {selectedFilename}
                        </div>
                    ) : null}
                    {parseErrorMessage !== null ? (
                        <Alert variant="destructive">
                            <AlertDescription>{parseErrorMessage}</AlertDescription>
                        </Alert>
                    ) : null}
                    <Button
                        onClick={runDryRun}
                        disabled={selectedBackup === null || dryRunMutation.isPending}
                    >
                        {dryRunMutation.isPending ? 'Running dry run...' : 'Dry run'}
                    </Button>
                    {dryRunMutation.error !== null ? (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {formatRestoreError(dryRunMutation.error, 'Dry run failed.')}
                            </AlertDescription>
                        </Alert>
                    ) : null}
                </CardContent>
            </Card>

            {dryRunResult !== undefined ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Dry Run Result</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="text-sm">Valid: {dryRunResult.valid ? 'Yes' : 'No'}</div>
                        <div className="overflow-x-auto">
                            <table
                                className="w-full text-left text-sm"
                                aria-label="Dry-run collection summary"
                            >
                                <thead>
                                    <tr className="border-b">
                                        <th scope="col" className="py-2 pr-4 font-medium">
                                            Collection
                                        </th>
                                        <th scope="col" className="py-2 pr-4 font-medium">
                                            Records
                                        </th>
                                        {MERCHANT_BACKUP_SUMMARY_ACTIONS.map((action) => (
                                            <th
                                                key={action}
                                                scope="col"
                                                className="py-2 pr-4 font-medium capitalize"
                                            >
                                                {action}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {MERCHANT_BACKUP_SUMMARY_ROWS.map((row) => {
                                        const collectionSummary =
                                            dryRunResult.collection_summaries?.[row.key];
                                        return (
                                            <tr key={row.key} className="border-b last:border-0">
                                                <th scope="row" className="py-2 pr-4 font-normal">
                                                    {row.label}
                                                </th>
                                                <td className="py-2 pr-4">
                                                    {dryRunResult.summary[row.key]}
                                                </td>
                                                {MERCHANT_BACKUP_SUMMARY_ACTIONS.map((action) => {
                                                    const count = collectionSummary?.[action] ?? 0;
                                                    return (
                                                        <td
                                                            key={action}
                                                            className={`py-2 pr-4 ${
                                                                action === 'excluded' && count > 0
                                                                    ? 'font-semibold text-destructive'
                                                                    : ''
                                                            }`}
                                                        >
                                                            {count}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {dryRunResult.warnings.length > 0 ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    <div className="space-y-1">
                                        <div className="font-semibold">
                                            {dryRunResult.valid
                                                ? 'Restore warnings'
                                                : 'Restore cannot be applied'}
                                        </div>
                                        {dryRunResult.warnings.map(
                                            (warning: string, index: number) => (
                                                <div key={`${warning}-${index}`}>- {warning}</div>
                                            )
                                        )}
                                    </div>
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        <span className="inline-flex" title={applyRestoreTooltip}>
                            <Button
                                variant="destructive"
                                disabled={applyRestoreDisabled}
                                title={applyRestoreTooltip}
                                onClick={() => {
                                    if (applyRestoreDisabled) {
                                        return;
                                    }
                                    setShowApplyConfirmation(true);
                                }}
                            >
                                {applyMutation.isPending ? 'Restoring...' : 'Apply restore'}
                            </Button>
                        </span>
                        {applyMutation.error !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {formatRestoreError(applyMutation.error, 'Restore failed.')}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {applyMutation.data !== undefined ? (
                            <div className="text-sm">
                                Restore completed. New organization:{' '}
                                <Link
                                    className="text-primary underline"
                                    to={`/organizations/${applyMutation.data.target_organization_uuid}?view=merchant`}
                                >
                                    {applyMutation.data.target_organization_name}
                                </Link>
                            </div>
                        ) : null}
                    </CardContent>
                </Card>
            ) : null}

            <ConfirmationModal
                open={showApplyConfirmation}
                onOpenChange={setShowApplyConfirmation}
                message="Apply restore and create a new organization from this backup?"
                confirmLabel="Restore"
                onConfirm={confirmApplyRestore}
                isLoading={applyMutation.isPending}
            />
        </div>
    );
}
